/**
 * The full-screen CREATION experience (feature 0059 — founder direction 2026-08-20): from the
 * dashboard's AI card the seller lands here, watches her store being BUILT FROM A BLANK STAGE, and
 * ends on two doors — «Explorer ma boutique» (walk it as a customer) or «Ouvrir le builder» (edit).
 *
 * Three phases, one component:
 *  1. CURTAIN — an opaque animated stage while the theme seeds and the first section is being
 *     written. The seller never sees a ready-made theme appear (that read as "nothing was generated"
 *     in the founder's first test); the store exists only once ITS OWN words start landing.
 *  2. BUILD — the curtain lifts: the live canvas IS the show, framed by a slim top bar naming the
 *     work («Écrit les questions fréquentes…»). The editor chrome is hidden (BuilderShell), nothing
 *     is clickable-away mid-run — this is the «modal où on suit le rendu».
 *  3. DONE — a centered card: the tally, the «à toi de remplir» list, the two doors.
 */
import { useGenerationStore } from '../state/generation/generationStore';
import { bt, btp } from '../i18n';

const INTEGRITY_LABELS: Record<string, string> = {
  testimonials: bt('chrome.gen.int_testimonials', 'Customer reviews'),
  'trustpilot-reviews': bt('chrome.gen.int_trustpilot', 'Trustpilot reviews'),
  'facebook-testimonials': bt('chrome.gen.int_facebook', 'Facebook reviews'),
  results: bt('chrome.gen.int_results', 'Numbers and results'),
  'spec-comparison': bt('chrome.gen.int_comparison', 'The comparison table'),
  'comparison-table': bt('chrome.gen.int_comparison', 'The comparison table'),
  'custom-html': bt('chrome.gen.int_html', 'The custom HTML block'),
};

const PAGE_ROWS: ReadonlyArray<{ template: string; label: string }> = [
  { template: 'product', label: bt('chrome.tk.page_product', 'Product page') },
  { template: 'index', label: bt('chrome.tk.page_home', 'Home') },
];

export function GenerationTakeover() {
  const mode = useGenerationStore((s) => s.mode);
  const status = useGenerationStore((s) => s.status);
  const activity = useGenerationStore((s) => s.activity);
  const currentPage = useGenerationStore((s) => s.currentPage);
  const sections = useGenerationStore((s) => s.sections);
  const summary = useGenerationStore((s) => s.summary);
  const productPhotos = useGenerationStore((s) => s.productPhotos);
  const pageStates = useGenerationStore((s) => s.pageStates);
  const integritySkips = useGenerationStore((s) => s.integritySkips);
  const explore = useGenerationStore((s) => s.explore);
  const publishStore = useGenerationStore((s) => s.publishStore);
  const publishState = useGenerationStore((s) => s.publishState);
  const storefrontUrl = useGenerationStore((s) => s.storefrontUrl);
  const openEditor = useGenerationStore((s) => s.openEditor);

  if (mode !== 'takeover' || status === 'idle' || status === 'armed') return null;

  const written = sections.filter((s) => s.state === 'done').length;
  // Phase 1 ONLY before the first page ever starts. It used to key on the CURRENT page's counter,
  // which resets between pages — so the opening curtain came back at each transition and read as
  // «il a recommencé à zéro» (founder 2026-08-20). Once any page has started, the bar + rail stay.
  const anyPageStarted = Object.values(pageStates).some((p) => p !== 'pending');
  const curtain = status === 'running' && !anyPageStarted;

  if (status === 'running' && curtain) {
    return (
      <div className="bld-tk bld-tk--curtain" role="status" aria-live="polite">
        <div className="bld-tk__stage">
          <span className="bld-tk__spark" aria-hidden="true">
            ✦
          </span>
          <h1 className="bld-tk__title">{bt('chrome.tk.building', 'Building your store')}</h1>
          <p className="bld-tk__activity">
            {activity ?? bt('chrome.tk.sketching', 'Sketching the structure…')}
          </p>
          <div className="bld-tk__bones" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'running') {
    return (
      <div className="bld-tk bld-tk--bar" role="status" aria-live="polite">
        <div className="bld-tk__topbar">
          <span className="bld-tk__spinner" aria-hidden="true" />
          <strong>
            {activity ??
              (written === 0 && currentPage
                ? btp('chrome.tk.preparing', 'Preparing {page}…', {
                    page: currentPage.toLowerCase(),
                  })
                : bt('chrome.tk.building_ellipsis', 'Building your store…'))}
          </strong>
          <span className="bld-tk__meta">
            {currentPage}
            {written > 0 &&
              ` · ${btp('chrome.tk.sections_n', '{count} sections', { count: written })}`}
          </span>
        </div>
        {/* The page rail (the Lightfunnels side panel, ours): which page is being built, which are
            waiting, which are done — the seller reads the whole plan while watching it happen. */}
        <aside
          className="bld-tk__rail"
          aria-label={bt('chrome.tk.rail_aria', 'Your store’s pages')}
        >
          <p className="bld-tk__railTitle">{bt('chrome.tk.rail_title', 'Your store')}</p>
          {PAGE_ROWS.map(({ template, label }) => {
            const state = pageStates[template] ?? 'pending';
            if (state === 'absent') return null;
            return (
              <div key={template} className={`bld-tk__railRow bld-tk__railRow--${state}`}>
                <span className="bld-tk__railDot" aria-hidden="true" />
                <span className="bld-tk__railLabel">{label}</span>
                <span className="bld-tk__railState">
                  {state === 'done'
                    ? bt('chrome.tk.state_done', 'Done')
                    : state === 'running'
                      ? bt('chrome.tk.state_running', 'In progress…')
                      : bt('chrome.tk.state_pending', 'Waiting')}
                </span>
              </div>
            );
          })}
        </aside>
      </div>
    );
  }

  // done | unavailable
  return (
    <div
      className="bld-tk bld-tk--end"
      role="dialog"
      aria-modal="true"
      aria-label={bt('chrome.tk.ready_aria', 'Store ready')}
    >
      <div className="bld-tk__card">
        {status === 'unavailable' ? (
          <>
            <h1 className="bld-tk__title">{bt('chrome.tk.stopped', 'Generation stopped')}</h1>
            <p className="bld-tk__hint">
              {bt(
                'chrome.tk.stopped_hint',
                'The generation service isn’t responding (missing key, provider down or credits exhausted). Nothing was billed for what failed — try again in a few minutes, your store’s skeleton stays editable.',
              )}
            </p>
          </>
        ) : (
          <>
            <h1 className="bld-tk__title">{bt('chrome.tk.ready', 'Your store is ready')}</h1>
            <p className="bld-tk__hint">
              {btp('chrome.tk.written_n', '{count} sections written', {
                count: summary?.sectionsWritten ?? 0,
              })}
              {summary && summary.imagesGenerated > 0 && (
                <>
                  {', '}
                  {btp('chrome.tk.images_n', '{count} images generated', {
                    count: summary.imagesGenerated,
                  })}
                </>
              )}
              {productPhotos > 0 && (
                <>
                  {', '}
                  {btp('chrome.tk.photos_n', '{count} product photos added to your media', {
                    count: productPhotos,
                  })}
                </>
              )}
              {bt(
                'chrome.tk.from_brief',
                ', from your description. Everything can be retouched, everything undoes (Ctrl-Z).',
              )}
              {summary?.imagesUnavailable && (
                <>
                  {' '}
                  {bt(
                    'chrome.tk.images_kept',
                    'The theme’s images were kept — the image key isn’t configured yet.',
                  )}
                </>
              )}
            </p>
            {integritySkips.length > 0 && (
              <div className="bld-tk__integrity">
                <p>{bt('chrome.gen.integrity', 'Yours to fill in — we never invent:')}</p>
                <ul>
                  {[...new Set(integritySkips.map((t) => INTEGRITY_LABELS[t] ?? t))].map(
                    (label) => (
                      <li key={label}>{label}</li>
                    ),
                  )}
                </ul>
              </div>
            )}
          </>
        )}
        {publishState === 'published' && (
          <p className="bld-tk__published">
            {bt('chrome.tk.published', 'Your store is live.')}{' '}
            {storefrontUrl && (
              <a href={storefrontUrl} target="_blank" rel="noreferrer">
                {bt('chrome.tk.view_store', 'See my store')}
              </a>
            )}
          </p>
        )}
        {publishState === 'failed' && (
          <p className="bld-tk__publishError">
            {bt(
              'chrome.tk.publish_failed',
              'Going live failed — try again, or publish from the builder.',
            )}
          </p>
        )}
        <div className="bld-tk__actions">
          <button type="button" className="bld-tk__btn bld-tk__btn--ghost" onClick={openEditor}>
            {bt('chrome.tk.open_builder', 'Open the builder')}
          </button>
          <button type="button" className="bld-tk__btn bld-tk__btn--ghost" onClick={explore}>
            {bt('chrome.tk.explore', 'Explore my store')}
          </button>
          {status !== 'unavailable' && publishState !== 'published' && (
            <button
              type="button"
              className="bld-tk__btn"
              onClick={() => void publishStore()}
              disabled={publishState === 'publishing'}
            >
              {publishState === 'publishing'
                ? bt('chrome.tk.publishing', 'Going live…')
                : bt('chrome.tk.publish', 'Put my store online')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
